import { useGetPresentationQuery } from '@carboncopyco/data';
import { CopyOutlineIcon, SettingsIcon } from '@carboncopyco/icons';
import type { DemoShare } from '@carboncopyco/types';
import { Button, Loading, TooltipBubble } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  getDaysFromNow,
  isFuture,
  useAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useState } from 'react';
import type { ShareDemoModalState } from '../../../Share/ShareDemoModal';
import { ShareDemoModal } from '../../../Share/ShareDemoModal';
import {
  UpdateSecurityLevelWarningModal,
  shouldShowSecurityLevelWarning,
} from '../../../Share/UpdateSecurityLevelWarningModal';
import { useAllowedShareSecurityLevels } from '../../../Share/useAllowedShareSecurityLevels';
import { useCopy } from '../../../utils';
import { useDemoPageProps } from '../DemoPageContext';
import { disabledDueToSharingPolicyText } from './consts';
import { useShowtimeUrl } from '@carboncopyco/components';

type LinkActionsProps = {
  link: DemoShare;
  onSubmit: (values: ShareDemoModalState, link: DemoShare) => Promise<void>;
  isLoading: boolean;
};

const disabledLinkText = {
  disabledDueToSharingPolicy: disabledDueToSharingPolicyText,
  disabledDueToDeprecatedSecurityLevel:
    'This is a public link anyone can access. Creating or updating it is no longer supported, you can only deactivate it. Check out other available sharing options.',
  disabledDueToExpiredLink: "Expired links can't be edited",
  disabledDueToShareSettings: 'Share settings',
};

export const LinkActions = ({
  link,
  onSubmit,
  isLoading,
}: LinkActionsProps) => {
  const flags = useFlags();
  const { showtimeShareUrl } = useShowtimeUrl();
  const { organizationId, demoId } = useDemoPageProps();
  const { trackEvent } = useAnalytics();
  const { copyText } = useCopy();
  const { data: presentation } = useGetPresentationQuery({
    organizationId,
    id: String(link.entityId),
  });
  const [showShareDemoModal, setShowShareDemoModal] = useBoolean();
  const [
    showUpdateSecurityLevelWarning,
    {
      setTrue: setTrueShowUpdateSecurityLevelWarning,
      setFalse: setFalseShowUpdateSecurityLevelWarning,
    },
  ] = useBoolean(false);
  const [formValues, setFormValues] = useState<ShareDemoModalState>();
  const { allowedDemoSecurityLevels } =
    useAllowedShareSecurityLevels(organizationId);

  const {
    options,
    options: {
      security,
      expiresAt,
      daysToExpire,
      integrations,
      skipWelcome,
      welcomeMatTitle,
    },
    isActive,
    entityMetadata: { forClient },
  } = link;

  const fallbackDaysToExpire =
    (expiresAt && isFuture(expiresAt) && getDaysFromNow(expiresAt)) || null;
  const expired = expiresAt && !isFuture(expiresAt);
  const getReasonForDisabledLink = useCallback<
    () => keyof typeof disabledLinkText
  >(() => {
    if (!allowedDemoSecurityLevels.includes(link.options.security.level)) {
      return 'disabledDueToSharingPolicy';
    }
    if (
      link.options.security.level === 'anyone' &&
      flags.deprecateAnyoneSecurityLevel
    ) {
      return 'disabledDueToDeprecatedSecurityLevel';
    }
    if (expired) {
      return 'disabledDueToExpiredLink';
    }
    return;
  }, [allowedDemoSecurityLevels, expired, flags.deprecateAnyoneSecurityLevel]);
  const isDisabledReason = getReasonForDisabledLink();
  const isDisabledTooltipMessage = disabledLinkText[isDisabledReason];
  const salesforceLeads = !!integrations?.find((int) => int === 'salesforce');
  const hubspotLeads = !!integrations?.find((int) => int === 'hubspot');
  const slackIntegration = !!integrations?.find((int) => int === 'slack');
  const shareData: ShareDemoModalState = {
    salesforceLeads,
    hubspotLeads,
    slackIntegration,
    linkName: options.linkDescription,
    client: forClient,
    isActive,
    variables: presentation?.variables ?? [],
    toggleExpiration: !!options.expiresAt && isFuture(options.expiresAt),
    ...options,
    expiresAt,
    daysToExpire: daysToExpire || fallbackDaysToExpire,
    skipWelcome,
    security: {
      ...security,
      level: security.level,
    },
    showGuidanceTooltip: options.showGuidanceTooltip,
    cover: skipWelcome
      ? { requireName: false, requireEmail: false }
      : options.welcomeMatConfig,
    welcomeMatTitle,
  };

  if (showUpdateSecurityLevelWarning) {
    return (
      <UpdateSecurityLevelWarningModal
        show={showUpdateSecurityLevelWarning}
        isLoading={isLoading}
        onConfirm={async () => {
          if (formValues) {
            await onSubmit(formValues, link);
            setShowShareDemoModal.setFalse();
            setFalseShowUpdateSecurityLevelWarning();
          }
        }}
        onClose={setFalseShowUpdateSecurityLevelWarning}
      />
    );
  }

  const location = 'demo_info_page_shares_tab_table_side_actions';
  const linkIsInactive = !link.isActive || !!expired;

  return (
    <div className="flex flex-row gap-3">
      {isLoading && <Loading color="text-ds-primary" />}
      <TooltipBubble
        title={linkIsInactive ? 'This link is inactive' : 'Copy link'}
        placement="top"
      >
        <Button
          data-testid="share-copy-link"
          aria-label="Copy link"
          disabled={linkIsInactive}
          dark
          icon={<CopyOutlineIcon />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.DEMO_CLICK_COPY_SHARE_LINK, {
              location,
            });
            copyText(
              showtimeShareUrl(link.token),
              'Link was copied to your clipboard'
            );
          }}
        />
      </TooltipBubble>
      <TooltipBubble
        title={isDisabledTooltipMessage}
        placement="top"
      >
        <Button
          data-testid="share-settings"
          disabled={!!isDisabledReason}
          dark
          icon={<SettingsIcon />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.DEMO_CLICK_SHARE_SETTINGS, {
              location,
            });
            setShowShareDemoModal.setTrue();
          }}
        />
      </TooltipBubble>
      {showShareDemoModal && (
        <ShareDemoModal
          demoId={demoId}
          yesLabel="Save changes"
          title="Edit link settings"
          initialValues={shareData}
          handleSubmit={async (values, { setSubmitting }) => {
            if (
              shouldShowSecurityLevelWarning(
                security.level,
                values.security.level
              )
            ) {
              setTrueShowUpdateSecurityLevelWarning();
              setFormValues(values);
              setSubmitting(false);
              return;
            }
            await onSubmit(values, link);
            setShowShareDemoModal.setFalse();
            setFalseShowUpdateSecurityLevelWarning();
          }}
          open={showShareDemoModal}
          onClose={setShowShareDemoModal.setFalse}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
